// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { fetchVisitCount } from '../../utils/visitTracking';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import TurtleIcon from '../AssistantWidget/TurtleIcon';
import styles from './footer.module.scss';
import packageJson from '../../../package.json';

// Same breakpoint footer.module.scss already switches to the stacked,
// centered mobile layout at - below it, the full link list is too much
// text for a footer and folds behind a toggle instead.
const MOBILE_FOOTER_QUERY = '(max-width: 576px)';

function openCookieSettings(event: React.MouseEvent<HTMLAnchorElement>) {
    const consentWindow = window as Window & {
        _iub?: { cs?: { api?: { openPreferences?: () => void } } };
    };
    const api = consentWindow._iub?.cs?.api;
    if (typeof api?.openPreferences === "function") {
        event.preventDefault();
        api.openPreferences();
    }
}

const FooterComponent = () => {
    const currentYear = new Date().getFullYear();
    const version = `v${packageJson.version}`;
    const [siteVisits, setSiteVisits] = useState(null);
    const isMobileFooter = useMediaQuery(MOBILE_FOOTER_QUERY);
    const [isLinksOpen, setIsLinksOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setIsLinksOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        let cancelled = false;

        fetchVisitCount()
            .then((visits) => {
                if (!cancelled && visits !== null) {
                    setSiteVisits(visits);
                }
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <footer className={styles.container}>
            <div className={styles.footerContent}>
                <div className={styles.topRow}>
                    <div className={styles.brandSection}>
                        <div className={styles.brandRow}>
                            <span className={styles.brandMark}><TurtleIcon size={32} animated={false} /></span>
                            <span className={styles.brand}>NihongoTech</span>
                            <span className={styles.version}>{version}</span>
                            {siteVisits !== null && (
                                <span className={styles.visitCount} title="Site visits">
                                    <FontAwesomeIcon icon={faEye} className={styles.visitCountIcon} />
                                    {siteVisits.toLocaleString()}
                                </span>
                            )}
                        </div>
                        <span className={styles.tagline}>日本語オンラインキーボード</span>
                    </div>

                    <div className={styles.linksSection}>
                    {isMobileFooter && (
                        <button
                            type="button"
                            className={styles.linksToggle}
                            onClick={() => setIsLinksOpen((open) => !open)}
                            aria-expanded={isLinksOpen}
                            aria-controls="footer-links"
                        >
                            {isLinksOpen ? 'Show less' : 'Show more'}
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className={isLinksOpen ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron}
                            />
                        </button>
                    )}
                    {(!isMobileFooter || isLinksOpen) && (
                    <nav id="footer-links" className={styles.linksRow} aria-label="Footer">
                        <Link to="/" className={styles.footerLink}>
                            Home
                        </Link>

                        <Link to="/lessons" className={styles.footerLink}>
                            Learn
                        </Link>

                        <Link to="/#practice" className={styles.footerLink}>
                            Practice
                        </Link>

                        <Link to="/keyboard" className={styles.footerLink}>
                            Keyboard
                        </Link>

                        <Link to="/grammar" className={styles.footerLink}>
                            Grammar
                        </Link>

                        <Link to="/vocabulary" className={styles.footerLink}>
                            Vocabulary
                        </Link>

                        <Link to="/conjugation" className={styles.footerLink}>
                            Conjugation
                        </Link>

                        <Link to="/wanikani" className={styles.footerLink}>
                            WaniKani
                        </Link>

                        <Link to="/converter" className={styles.footerLink}>
                            Hiragana / Kanji Converter
                        </Link>
                        <Link to="/romaji-converter" className={styles.footerLink}>
                            Romaji to Kana Converter
                        </Link>
                        <Link to="/blog" className={styles.footerLink}>
                            Blog
                        </Link>
                        <Link to="/about" className={styles.footerLink}>
                            About
                        </Link>
                        <Link to="/study-materials" className={styles.footerLink}>
                            Study Materials
                        </Link>
                        <Link to="/releases" className={styles.footerLink}>
                            Release Notes
                        </Link>

                        <Link to="/newsletter" className={styles.footerLink}>
                            Newsletter
                        </Link>

                        <Link to="/privacy-policy" className={styles.footerLink}>
                            Privacy Policy
                        </Link>

                        <a
                            href="https://www.iubenda.com/privacy-policy/46335044/cookie-policy"
                            className={styles.footerLink}
                            onClick={openCookieSettings}
                        >
                            Cookie settings
                        </a>

                        <a
                            href="mailto:admin@teqseq.com?subject=Kana%20Learn%20bug%20report"
                            className={styles.footerLink}
                        >
                            Report a Bug
                        </a>
                    </nav>
                    )}
                    </div>
                </div>
                <div className={styles.metaRow}>
                    <span className={styles.madeWith}>
                        NihongoTech by{' '}
                        <a
                            href="https://teqseq.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.brandLink}
                        >
                            TeqSeq
                        </a>
                    </span>
                    <span className={styles.copyright}>© {currentYear} All rights reserved.</span>
                </div>
            </div>
        </footer>
    );
};

export default FooterComponent;
